import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export type MenuItem =
  | { type: 'separator' }
  | {
      type?: 'item'
      label: string
      onClick?: () => void
      submenu?: MenuItem[]
      danger?: boolean
      disabled?: boolean
      checked?: boolean
    }

export interface MenuState { x: number; y: number; items: MenuItem[] }

const CHEVRON = (
  <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round"><path d="M6 3.5 10.5 8 6 12.5" /></svg>
)
const CHECK = (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.2"
       strokeLinecap="round" strokeLinejoin="round"><path d="M3 8.5 6.5 12 13 4.5" /></svg>
)

function Panel({ items, x, y, onClose, nested = false }: {
  items: MenuItem[]; x: number; y: number; onClose: () => void; nested?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ x, y })
  const [openSub, setOpenSub] = useState<{ index: number; x: number; y: number } | null>(null)

  // Keep the panel inside the window, flipping the way AppKit menus do.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    let nx = x
    let ny = y
    if (x + r.width > window.innerWidth - 8) nx = nested ? x - r.width - 4 : window.innerWidth - r.width - 8
    if (y + r.height > window.innerHeight - 8) ny = Math.max(8, window.innerHeight - r.height - 8)
    setPos({ x: Math.max(8, nx), y: Math.max(8, ny) })
  }, [x, y, nested])

  return (
    <div ref={ref} className="ctx-menu" style={{ left: pos.x, top: pos.y }} role="menu">
      {items.map((item, i) => {
        if ('type' in item && item.type === 'separator') return <div key={i} className="ctx-sep" />
        const it = item as Extract<MenuItem, { label: string }>
        const hasSub = !!it.submenu?.length
        return (
          <div
            key={i}
            role="menuitem"
            className={`ctx-item ${it.danger ? 'danger' : ''} ${it.disabled ? 'disabled' : ''}`}
            onMouseEnter={(e) => {
              if (!hasSub) { setOpenSub(null); return }
              const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
              setOpenSub({ index: i, x: r.right - 2, y: r.top - 5 })
            }}
            onClick={(e) => {
              e.stopPropagation()
              if (it.disabled || hasSub) return
              it.onClick?.()
              onClose()
            }}
          >
            <span className="ctx-check">{it.checked ? CHECK : null}</span>
            <span className="ctx-label">{it.label}</span>
            {hasSub && <span className="ctx-chevron">{CHEVRON}</span>}
            {hasSub && openSub?.index === i && createPortal(
              // Portalled to <body>: .ctx-menu's backdrop-filter makes it a
              // containing block for fixed children, which would otherwise
              // resolve the submenu's viewport coords against the parent menu.
              <Panel items={it.submenu!} x={openSub.x} y={openSub.y} onClose={onClose} nested />,
              document.body,
            )}
          </div>
        )
      })}
    </div>
  )
}

export function ContextMenu({ state, onClose }: { state: MenuState | null; onClose: () => void }) {
  useEffect(() => {
    if (!state) return
    const close = (e: MouseEvent) => {
      // A mousedown ON the menu must not dismiss it: mousedown fires before
      // click, so unmounting here meant the item's onClick never ran and every
      // menu action silently did nothing. Submenus are portalled to <body>,
      // so match on the class rather than a single container.
      if ((e.target as Element | null)?.closest?.('.ctx-menu')) return
      onClose()
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    const onResize = () => onClose()
    // Capture phase so a click elsewhere dismisses before it activates something else.
    window.addEventListener('mousedown', close, true)
    window.addEventListener('resize', onResize)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', close, true)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('keydown', onKey)
    }
  }, [state, onClose])

  if (!state) return null
  // Portalled to <body> for the same reason the submenus are: an ancestor with
  // backdrop-filter becomes the containing block for position: fixed, so a menu
  // rendered inside the sidebar was positioned against the sidebar's 224px box
  // and clipped by it. The viewport-flipping maths assumes the viewport.
  return createPortal(
    <div className="ctx-layer" onContextMenu={(e) => e.preventDefault()}>
      <Panel items={state.items} x={state.x} y={state.y} onClose={onClose} />
    </div>,
    document.body,
  )
}

/** Turns a right-click into menu coordinates. */
export function menuPos(e: React.MouseEvent): { x: number; y: number } {
  e.preventDefault()
  e.stopPropagation()
  return { x: e.clientX, y: e.clientY }
}
